import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, RefreshCcw, LogOut, Inbox, FileText, Mail, Ticket, RotateCcw, PenLine, Trash2 } from "lucide-react";
import { api, adminHeaders, setAdminToken, clearAdminToken, getAdminToken } from "@/lib/api";
import { Reveal } from "@/components/motion";

const TYPE_LABELS = { ontario: "Ontario based", out_of_province: "Out-of-province (Canada)", international: "International Students" };

const APP_STATUS = ["new", "contacted", "enrolled", "archived"];
const MSG_STATUS = ["new", "resolved"];
const SVC_STATUS = ["new", "processing", "completed", "archived"];

export default function Admin() {
  const [authed, setAuthed] = useState(!!getAdminToken());
  const [password, setPassword] = useState("");
  const [tab, setTab] = useState("applications");
  const [applications, setApplications] = useState([]);
  const [serviceOrders, setServiceOrders] = useState([]);
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [couponDays, setCouponDays] = useState(30);
  const [couponForm, setCouponForm] = useState({ code: "", discount_type: "percent", discount_value: "" });
  const [posts, setPosts] = useState([]);
  const [postEditing, setPostEditing] = useState(null);
  const [postForm, setPostForm] = useState({ title: "", slug: "", category: "", excerpt: "", body: "", published: true });

  const loadAll = async () => {
    try {
      const [a, s, m, st] = await Promise.all([
        api.get("/admin/applications", { headers: adminHeaders() }),
        api.get("/admin/service-orders", { headers: adminHeaders() }),
        api.get("/admin/messages", { headers: adminHeaders() }),
        api.get("/admin/stats", { headers: adminHeaders() }),
      ]);
      setApplications(a.data);
      setServiceOrders(s.data);
      setMessages(m.data);
      setStats(st.data);
    } catch (e) {
      if (e.response?.status === 401) {
        clearAdminToken();
        setAuthed(false);
      }
    }
  };

  const loadCoupons = async (days = couponDays) => {
    try {
      const res = await api.get(`/admin/coupons?days=${days}`, { headers: adminHeaders() });
      setCoupons(res.data);
    } catch (e) {
      if (e.response?.status === 401) {
        clearAdminToken();
        setAuthed(false);
      }
    }
  };

  useEffect(() => {
    if (authed) loadAll();
  }, [authed]);

  useEffect(() => {
    if (authed && tab === "coupons") loadCoupons(couponDays);
    if (authed && tab === "posts") loadPosts();
  }, [authed, tab, couponDays]);

  const loadPosts = async () => {
    try {
      const res = await api.get("/admin/posts", { headers: adminHeaders() });
      setPosts(res.data);
    } catch (e) {
      if (e.response?.status === 401) {
        clearAdminToken();
        setAuthed(false);
      }
    }
  };

  const savePost = async (e) => {
    e.preventDefault();
    try {
      if (postEditing) {
        await api.put(`/admin/posts/${postEditing}`, postForm, { headers: adminHeaders() });
        toast.success("Post updated.");
      } else {
        await api.post("/admin/posts", postForm, { headers: adminHeaders() });
        toast.success("Post published.");
      }
      setPostForm({ title: "", slug: "", category: "", excerpt: "", body: "", published: true });
      setPostEditing(null);
      loadPosts();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Could not save the post.");
    }
  };

  const editPost = (p) => {
    setPostEditing(p.id);
    setPostForm({ title: p.title, slug: p.slug, category: p.category, excerpt: p.excerpt, body: p.body, published: p.published });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deletePost = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/posts/${p.id}`, { headers: adminHeaders() });
      toast.success("Post deleted.");
      loadPosts();
    } catch {
      toast.error("Could not delete the post.");
    }
  };

  const createCoupon = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/coupons", { ...couponForm, discount_value: Number(couponForm.discount_value) }, { headers: adminHeaders() });
      toast.success(`Partner code ${couponForm.code.toUpperCase()} created.`);
      setCouponForm({ code: "", discount_type: "percent", discount_value: "" });
      loadCoupons();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create the code.");
    }
  };

  const toggleCoupon = async (id) => {
    try {
      await api.patch(`/admin/coupons/${id}`, {}, { headers: adminHeaders() });
      loadCoupons();
    } catch {
      toast.error("Update failed.");
    }
  };

  const refundApplication = async (id) => {
    try {
      await api.patch(`/admin/applications/${id}/refund`, {}, { headers: adminHeaders() });
      toast.success("Refund recorded.");
      loadCoupons();
    } catch {
      toast.error("Could not record the refund.");
    }
  };

  const login = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/admin/login", { password });
      setAdminToken(res.data.token);
      setAuthed(true);
      toast.success("Welcome back.");
    } catch {
      toast.error("Incorrect password.");
    }
  };

  const updateStatus = async (kind, id, status) => {
    try {
      await api.patch(`/admin/${kind}/${id}`, { status }, { headers: adminHeaders() });
      toast.success("Status updated.");
      loadAll();
    } catch {
      toast.error("Update failed.");
    }
  };

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink px-4">
        <Reveal>
          <form onSubmit={login} className="w-full max-w-sm rounded-3xl bg-ink2 p-8 text-ivory" data-testid="admin-login-form">
            <img src="/logo.webp" alt="Eletech Global Ontario School crest" className="h-14 w-14 rounded-full bg-white ring-2 ring-white/20" data-testid="admin-logo" />
            <h1 className="mt-6 font-display text-2xl font-extrabold">Admin Portal</h1>
            <p className="mt-2 text-sm text-ivory/60">Eletech Global Ontario School — admissions console.</p>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin password"
              data-testid="admin-password-input"
              className="mt-6 w-full rounded-xl border border-ivory/20 bg-ivory/5 px-4 py-3 text-sm text-ivory placeholder:text-ivory/40 outline-none focus:border-brand"
            />
            <button type="submit" data-testid="admin-login-submit" className="btn-accent mt-4 w-full">
              Sign in
            </button>
          </form>
        </Reveal>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand/60 pb-20" data-testid="admin-dashboard">
      <div className="bg-ink pb-20 pt-12 text-ivory">
        <div className="container-x flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="eyebrow !text-brandlight">Admin Portal</p>
            <h1 className="mt-2 font-display text-3xl font-extrabold">Admissions console</h1>
          </div>
          <div className="flex gap-3">
            <button onClick={loadAll} data-testid="admin-refresh-btn" className="btn-navy-outline !px-5 !py-2.5">
              <RefreshCcw className="h-4 w-4" /> Refresh
            </button>
            <button
              onClick={() => { clearAdminToken(); setAuthed(false); }}
              data-testid="admin-logout-btn"
              className="btn-navy-outline !px-5 !py-2.5"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="container-x -mt-12">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: FileText, label: "Applications", value: stats?.applications ?? "–", testid: "admin-stat-applications" },
            { icon: Inbox, label: "New applications", value: stats?.newApplications ?? "–", testid: "admin-stat-new-applications" },
            { icon: Mail, label: "Messages", value: stats?.messages ?? "–", testid: "admin-stat-messages" },
            { icon: Inbox, label: "New messages", value: stats?.newMessages ?? "–", testid: "admin-stat-new-messages" },
          ].map((s) => (
            <div key={s.label} className="card-line p-6" data-testid={s.testid}>
              <s.icon className="h-5 w-5 text-brand" />
              <p className="mt-3 font-mono text-3xl font-bold text-ink">{s.value}</p>
              <p className="text-xs text-slate-500">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex gap-2" data-testid="admin-tabs">
          {[
            { id: "applications", label: `Applications (${applications.length})` },
            { id: "services", label: `Service Orders (${serviceOrders.length})` },
            { id: "messages", label: `Messages (${messages.length})` },
            { id: "coupons", label: "Partner Codes" },
            { id: "posts", label: "Blog Posts" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`chip ${tab === t.id ? "chip-active" : ""}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-6 space-y-4">
          {tab === "posts" && (
            <div className="space-y-4" data-testid="admin-posts-panel">
              <form onSubmit={savePost} className="card-line space-y-4 p-6" data-testid="admin-post-form">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-brand">
                  {postEditing ? "Editing post" : "Write a new post"}
                </p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="field-label">Title</label>
                    <input
                      value={postForm.title}
                      onChange={(e) => setPostForm((f) => ({ ...f, title: e.target.value }))}
                      className="field"
                      placeholder="Group A vs Group B: how it works"
                      data-testid="admin-post-title-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="field-label">Slug (optional — auto from title)</label>
                    <input
                      value={postForm.slug}
                      onChange={(e) => setPostForm((f) => ({ ...f, slug: e.target.value }))}
                      className="field"
                      placeholder="group-a-vs-group-b"
                      data-testid="admin-post-slug-input"
                    />
                  </div>
                  <div>
                    <label className="field-label">Category</label>
                    <input
                      value={postForm.category}
                      onChange={(e) => setPostForm((f) => ({ ...f, category: e.target.value }))}
                      className="field"
                      placeholder="University Pathways"
                      data-testid="admin-post-category-input"
                    />
                  </div>
                  <div className="flex items-end pb-1">
                    <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-ink" data-testid="admin-post-published-label">
                      <input
                        type="checkbox"
                        checked={postForm.published}
                        onChange={(e) => setPostForm((f) => ({ ...f, published: e.target.checked }))}
                        className="h-4 w-4"
                        data-testid="admin-post-published-check"
                      />
                      Published (visible on the blog)
                    </label>
                  </div>
                </div>
                <div>
                  <label className="field-label">Cover image URL (optional)</label>
                  <input
                    value={postForm.coverImage || ""}
                    onChange={(e) => setPostForm((f) => ({ ...f, coverImage: e.target.value }))}
                    className="field"
                    placeholder="https://images.pexels.com/…"
                    data-testid="admin-post-cover-input"
                  />
                </div>
                <div>
                  <label className="field-label">Excerpt (1–2 sentences shown on cards & Google)</label>
                  <input
                    value={postForm.excerpt}
                    onChange={(e) => setPostForm((f) => ({ ...f, excerpt: e.target.value }))}
                    className="field"
                    data-testid="admin-post-excerpt-input"
                    required
                  />
                </div>
                <div>
                  <label className="field-label">Body — blank line for a new paragraph, "## " for a heading, "- " for bullets</label>
                  <textarea
                    value={postForm.body}
                    onChange={(e) => setPostForm((f) => ({ ...f, body: e.target.value }))}
                    className="field min-h-[220px] font-mono text-sm"
                    data-testid="admin-post-body-input"
                    required
                  />
                </div>
                <div className="flex gap-2">
                  <button type="submit" data-testid="admin-post-save-btn" className="btn-accent">
                    {postEditing ? "Save changes" : "Publish post"}
                  </button>
                  {postEditing && (
                    <button
                      type="button"
                      onClick={() => {
                        setPostEditing(null);
                        setPostForm({ title: "", slug: "", category: "", excerpt: "", body: "", published: true });
                      }}
                      className="btn-ghost"
                      data-testid="admin-post-cancel-btn"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>

              {posts.map((p) => (
                <div key={p.id} className="card-line flex flex-wrap items-center justify-between gap-4 p-5" data-testid={`admin-post-row-${p.slug}`}>
                  <div>
                    <p className="font-display text-sm font-extrabold text-ink">{p.title}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      /blog/{p.slug} · {p.category || "Uncategorised"} ·{" "}
                      {p.published ? "Published" : "Draft"} · updated {new Date(p.updated_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => editPost(p)} className="chip" data-testid={`admin-post-edit-btn-${p.slug}`}>
                      <PenLine className="h-3.5 w-3.5" /> Edit
                    </button>
                    <button onClick={() => deletePost(p)} className="chip" data-testid={`admin-post-delete-btn-${p.slug}`}>
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {tab === "coupons" && (
            <div className="space-y-4" data-testid="admin-coupons-panel">
              <form onSubmit={createCoupon} className="card-line grid gap-4 p-6 sm:grid-cols-4" data-testid="admin-coupon-create-form">
                <div className="sm:col-span-1">
                  <label className="field-label">Code</label>
                  <input
                    value={couponForm.code}
                    onChange={(e) => setCouponForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                    className="field"
                    placeholder="EGOSPARTNER"
                    data-testid="admin-coupon-code-input"
                  />
                </div>
                <div>
                  <label className="field-label">Discount type</label>
                  <select
                    value={couponForm.discount_type}
                    onChange={(e) => setCouponForm((f) => ({ ...f, discount_type: e.target.value }))}
                    className="field"
                    data-testid="admin-coupon-type-select"
                  >
                    <option value="percent">Percentage off the total</option>
                    <option value="fixed">Fixed dollar amount off</option>
                  </select>
                </div>
                <div>
                  <label className="field-label">{couponForm.discount_type === "percent" ? "Percent (1–100)" : "Amount ($)"}</label>
                  <input
                    type="number"
                    min="1"
                    value={couponForm.discount_value}
                    onChange={(e) => setCouponForm((f) => ({ ...f, discount_value: e.target.value }))}
                    className="field"
                    placeholder={couponForm.discount_type === "percent" ? "10" : "50"}
                    data-testid="admin-coupon-value-input"
                  />
                </div>
                <div className="flex items-end">
                  <button type="submit" data-testid="admin-coupon-create-btn" className="btn-accent w-full">
                    <Ticket className="h-4 w-4" /> Create code
                  </button>
                </div>
              </form>

              <div className="flex flex-wrap items-center justify-between gap-3 px-1">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400">Usage in selected period</p>
                <select
                  value={couponDays}
                  onChange={(e) => setCouponDays(Number(e.target.value))}
                  data-testid="admin-coupon-period-select"
                  className="field !w-auto !py-2 text-sm"
                >
                  <option value={7}>Last 7 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={90}>Last 90 days</option>
                  <option value={0}>All time</option>
                </select>
              </div>

              {coupons.length === 0 ? (
                <div className="card-line p-14 text-center" data-testid="admin-empty-coupons">
                  <p className="text-sm text-slate-500">No partner codes yet — create one above to track its usage.</p>
                </div>
              ) : (
                coupons.map((c) => (
                  <div key={c.id} className="card-line p-6" data-testid={`admin-coupon-card-${c.code}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <p className="font-mono text-sm font-bold text-ink">
                          {c.code}{" "}
                          <span className="ml-2 rounded-full bg-brandlight px-2.5 py-0.5 text-xs font-bold text-ink">
                            {c.discount_type === "percent" ? `${c.discount_value}% off` : `$${c.discount_value} off`}
                          </span>
                          {!c.active && <span className="ml-2 rounded-full bg-slate-200 px-2.5 py-0.5 text-xs font-bold text-slate-500">inactive</span>}
                        </p>
                        <div className="mt-3 grid grid-cols-2 gap-x-8 gap-y-2 text-sm sm:grid-cols-4" data-testid={`admin-coupon-stats-${c.code}`}>
                          <p><span className="text-slate-500">Used</span> <strong className="text-ink">{c.uses}×</strong></p>
                          <p><span className="text-slate-500">Total amount</span> <strong className="text-ink">${c.total_amount}</strong></p>
                          <p><span className="text-slate-500">Payment status</span> <strong className="text-ink">{c.pending_count} pending · {c.refunded_count} refunded</strong></p>
                          <p><span className="text-slate-500">Refunded</span> <strong className="text-ink">${c.refunded_amount}</strong></p>
                        </div>
                      </div>
                      <button
                        onClick={() => toggleCoupon(c.id)}
                        data-testid={`admin-coupon-toggle-${c.code}`}
                        className="chip"
                      >
                        {c.active ? "Deactivate" : "Activate"}
                      </button>
                    </div>
                    {c.usages.length > 0 && (
                      <div className="mt-4 space-y-2 border-t border-slate-100 pt-4">
                        {c.usages.map((u) => (
                          <div key={u._id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-sand/60 px-4 py-2.5 text-xs" data-testid={`admin-coupon-usage-${u.ref_id}`}>
                            <span className="font-mono font-bold text-brand">{u.ref_id}</span>
                            <span className="text-slate-600">{u.student_name}</span>
                            <span className="text-slate-500">{new Date(u.created_at).toLocaleDateString("en-CA")}</span>
                            <span className="font-semibold text-ink">${u.total}</span>
                            {u.payment_status === "refunded" ? (
                              <span className="rounded-full bg-aqualight px-2.5 py-0.5 font-bold text-aqua">refunded — ${u.refunded_amount}</span>
                            ) : (
                              <span className="flex items-center gap-2">
                                <span className="rounded-full bg-slate-200 px-2.5 py-0.5 font-bold text-slate-500">pending</span>
                                <button
                                  onClick={() => refundApplication(u._id)}
                                  data-testid={`admin-coupon-refund-${u.ref_id}`}
                                  className="flex items-center gap-1 rounded-full border border-slate-300 px-2.5 py-0.5 font-bold text-slate-600 transition-colors hover:border-brand hover:text-brand"
                                >
                                  <RotateCcw className="h-3 w-3" /> Record refund
                                </button>
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {tab === "applications" &&
            (applications.length === 0 ? (
              <div className="card-line p-14 text-center" data-testid="admin-empty-applications">
                <p className="text-sm text-slate-500">No applications yet — submissions from the Apply page appear here in real time.</p>
              </div>
            ) : (
              applications.map((a) => (
                <div key={a.id} className="card-line p-6" data-testid={`admin-application-card-${a.ref_id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-mono text-xs font-bold text-brand">{a.ref_id} · {new Date(a.created_at).toLocaleDateString("en-CA")}</p>
                      <h3 className="mt-1 font-display text-lg font-bold text-ink">{a.student_name}</h3>
                      <p className="text-sm text-slate-500">{a.email} {a.phone && `· ${a.phone}`}</p>
                      <p className="mt-2 text-sm text-slate-700">
                        <strong>Grade {a.grade_level}</strong> · {TYPE_LABELS[a.student_type] || a.student_type} ·{" "}
                        {a.pathway === "ossd" ? "OSSD from EGOS" : a.pathway === "individual" ? "Individual credits only" : "—"} ·
                        Courses: {a.courses.join(", ")}
                      </p>
                      {(a.parent_name || a.address) && (
                        <p className="mt-1 text-xs text-slate-500" data-testid={`admin-application-details-${a.ref_id}`}>
                          {a.parent_name && <>Parent/Guardian: <strong>{a.parent_name}</strong> ({a.parent_email}) · </>}
                          {a.address}{a.province ? `, ${a.province}` : ""}, {a.country}
                          {a.plar_completed ? ` · PLAR with EGOS: ${a.plar_completed === "yes" ? "completed" : "not completed"}` : ""}
                          {a.current_sor_is_egos ? ` · EGOS is your current Full-Time school: ${a.current_sor_is_egos === "yes" ? "Yes" : "No"}` : ""}
                          {a.full_time_school_name ? ` · Registered full-time school: ${a.full_time_school_name}` : ""}
                          {a.osr_info ? ` · OSR: ${a.osr_info}` : ""}
                        </p>
                      )}
                      {a.fees?.length > 0 && (
                        <div className="mt-3 rounded-xl bg-sand/70 p-3 text-xs text-slate-600" data-testid={`admin-application-fees-${a.ref_id}`}>
                          {a.fees.map((f) => (
                            <p key={f.name} className="flex items-center justify-between gap-6">
                              <span>{f.name}</span>
                              <strong className="text-ink">${f.amount}</strong>
                            </p>
                          ))}
                          {a.bundled_discount > 0 && (
                            <p className="flex items-center justify-between gap-6 text-aqua">
                              <span>Tuition bundled discount</span>
                              <strong>-${a.bundled_discount}</strong>
                            </p>
                          )}
                          {a.coupon_code && (
                            <p className="flex items-center justify-between gap-6 text-aqua">
                              <span>Partner code {a.coupon_code}</span>
                              <strong>-${a.discount_amount}</strong>
                            </p>
                          )}
                          <p className="mt-2 flex items-center justify-between gap-6 border-t border-slate-200 pt-2 text-sm">
                            <strong className="text-ink">Total{a.payment_status === "refunded" ? " (refunded)" : ""}</strong>
                            <strong className="text-brand">${a.total}</strong>
                          </p>
                        </div>
                      )}
                      {a.message && <p className="mt-2 rounded-xl bg-sand/70 p-3 text-sm italic text-slate-600">“{a.message}”</p>}
                    </div>
                    <select
                      value={a.status}
                      onChange={(e) => updateStatus("applications", a.id, e.target.value)}
                      data-testid={`admin-application-status-${a.ref_id}`}
                      className="field !w-auto"
                    >
                      {APP_STATUS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ))
            ))}

          {tab === "services" &&
            (serviceOrders.length === 0 ? (
              <div className="card-line p-14 text-center" data-testid="admin-empty-services">
                <p className="text-sm text-slate-500">No service orders yet — PLAR, course-transfer and course-extension purchases from the Tuition page appear here.</p>
              </div>
            ) : (
              serviceOrders.map((o) => (
                <div key={o.id} className="card-line p-6" data-testid={`admin-service-card-${o.ref_id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-mono text-xs font-bold text-brand">{o.ref_id} · {new Date(o.created_at).toLocaleDateString("en-CA")}</p>
                      <h3 className="mt-1 font-display text-lg font-bold text-ink">{o.student_name}</h3>
                      <p className="text-sm text-slate-500">{o.email} {o.phone && `· ${o.phone}`}</p>
                      {(o.grade_level || o.parent_name) && (
                        <p className="mt-1 text-xs text-slate-500">
                          {o.grade_level && <>Grade level: <strong>{o.grade_level === "homeschool" ? "Homeschool" : `Entering Grade ${o.grade_level}`}</strong> · </>}
                          {o.parent_name && <>Parent/Guardian: <strong>{o.parent_name}</strong> ({o.parent_email}) · </>}
                          {o.address}{o.province ? `, ${o.province}` : ""}, {o.country}
                          {o.school_board ? ` · School board: ${o.school_board}` : ""}
                        </p>
                      )}
                      {o.items?.length > 0 && (
                        <div className="mt-3 rounded-xl bg-sand/70 p-3 text-xs text-slate-600" data-testid={`admin-service-fees-${o.ref_id}`}>
                          {o.fees.map((f) => (
                            <p key={f.name} className="flex items-center justify-between gap-6">
                              <span>{f.name}</span>
                              <strong className="text-ink">${f.amount}</strong>
                            </p>
                          ))}
                          {o.items.some((it) => it.type === "transfer" && it.oen) && (
                            <p className="mt-2">Transfer OEN: {o.items.filter((it) => it.type === "transfer" && it.oen).map((it) => it.oen).join(", ")}</p>
                          )}
                          <p className="mt-2 flex items-center justify-between gap-6 border-t border-slate-200 pt-2 text-sm">
                            <strong className="text-ink">Total</strong>
                            <strong className="text-brand">${o.total}</strong>
                          </p>
                        </div>
                      )}
                    </div>
                    <select
                      value={o.status}
                      onChange={(e) => updateStatus("service-orders", o.id, e.target.value)}
                      data-testid={`admin-service-status-${o.ref_id}`}
                      className="field !w-auto"
                    >
                      {SVC_STATUS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ))
            ))}

          {tab === "messages" &&
            (messages.length === 0 ? (
              <div className="card-line p-14 text-center" data-testid="admin-empty-messages">
                <p className="text-sm text-slate-500">No messages yet — contact form and newsletter submissions appear here.</p>
              </div>
            ) : (
              messages.map((m) => (
                <div key={m.id} className="card-line p-6" data-testid={`admin-message-card-${m.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-mono text-xs font-bold text-brand">{new Date(m.created_at).toLocaleDateString("en-CA")} {m.subject && `· ${m.subject}`}</p>
                      <h3 className="mt-1 font-display text-lg font-bold text-ink">{m.name}</h3>
                      <p className="text-sm text-slate-500">{m.email}</p>
                      <p className="mt-2 text-sm text-slate-700">{m.message}</p>
                    </div>
                    <select
                      value={m.status}
                      onChange={(e) => updateStatus("messages", m.id, e.target.value)}
                      data-testid={`admin-message-status-${m.id}`}
                      className="field !w-auto"
                    >
                      {MSG_STATUS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ))
            ))}
        </div>
      </div>
    </div>
  );
}
